import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Cookie } from "lucide-react";
import { initAnalytics, setAnalyticsConsent } from "../lib/analytics";
import { CONSENT_KEY } from "./AnalyticsBootstrap";

/**
 * Slim GDPR-compliant consent banner that appears bottom-left.
 * User can Accept all, Reject all, or dismiss (treated as reject).
 * Choice persists in localStorage and hides the banner on future visits.
 */
export function ConsentBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(CONSENT_KEY);
      if (!raw) {
        // Delay so the banner doesn't fight for attention with the hero.
        const t = window.setTimeout(() => setVisible(true), 1500);
        return () => window.clearTimeout(t);
      }
    } catch {
      /* ignore */
    }
  }, []);

  const persist = (next) => {
    try {
      window.localStorage.setItem(CONSENT_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
    setAnalyticsConsent(next);
    if (next.analytics || next.marketing) {
      initAnalytics(next);
    }
    setVisible(false);
  };

  const acceptAll = () => persist({ analytics: true, marketing: true });
  const rejectAll = () => persist({ analytics: false, marketing: false });

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="consent-banner"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-4 left-4 right-4 md:right-auto md:max-w-[420px] z-[90]"
        >
          <div className="relative bg-[#0C182E] text-[#F4EDE2] border border-[#C8A96A]/25 shadow-[0_20px_50px_rgba(0,0,0,0.4)] p-5 md:p-6">
            <button
              type="button"
              onClick={rejectAll}
              aria-label="Close and reject"
              data-testid="consent-close"
              className="absolute top-3 right-3 text-[#C8A96A]/60 hover:text-[#C8A96A] transition-colors"
            >
              <X size={16} strokeWidth={1.5} />
            </button>

            <div className="flex items-start gap-3">
              <Cookie
                size={18}
                className="text-[#C8A96A] shrink-0 mt-0.5"
                strokeWidth={1.5}
              />
              <div className="flex-1 pr-4">
                <p
                  className="text-[10px] tracking-[0.3em] uppercase text-[#C8A96A]"
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  A quiet note
                </p>
                <p
                  className="mt-2 text-sm leading-relaxed text-[#F4EDE2]/85"
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  We use cookies to understand how our collection moves — no
                  personal data sold, ever. Accept to help us tailor YABA to you.
                </p>
              </div>
            </div>

            <div className="mt-5 flex items-center gap-2">
              <button
                type="button"
                onClick={acceptAll}
                data-testid="consent-accept"
                className="flex-1 bg-[#C8A96A] hover:bg-[#E9CE95] text-[#0C182E] text-[11px] tracking-[0.22em] uppercase px-4 h-11 transition-colors"
                style={{ fontFamily: "Inter, sans-serif" }}
              >
                Accept all
              </button>
              <button
                type="button"
                onClick={rejectAll}
                data-testid="consent-reject"
                className="flex-1 border border-[#F4EDE2]/25 hover:border-[#F4EDE2]/50 text-[#F4EDE2] text-[11px] tracking-[0.22em] uppercase px-4 h-11 transition-colors"
                style={{ fontFamily: "Inter, sans-serif" }}
              >
                Reject
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
